import { useMemo } from 'react';

import { ITranslateMethod } from '@core/js/hooks/i18n/types';
import { fn, length, regexp, required } from '@core/js/utils/Validation/validationRules';

import { useConfigTag } from '@interlayer/js/app/hooks/useConfigTag';
import { useRegistrationSelector } from '@interlayer/js/app/store/hooks/useRegistrationSelector';
import { CONFIG_TAG_TYPE } from '@interlayer/js/components/system/TagManager/buzz/constants';
import { TOccupationConfigTagConfig } from '@interlayer/js/components/system/TagManager/buzz/types';

import { TUseCheckOccupationProps } from '../types';

export const useCheckOccupation = (
    t: ITranslateMethod,
    { withOccupationExternal, isOccupationMandatoryExternal }: TUseCheckOccupationProps = {}
) => {
    const {
        occupationSuggestions,
        config: {
            withOccupation = withOccupationExternal,
            isOccupationMandatory = isOccupationMandatoryExternal
        }
    } = useRegistrationSelector();
    const occupationConfig = useConfigTag(CONFIG_TAG_TYPE.occupationConfig) as TOccupationConfigTagConfig;
    const { config: { additionalOccupationOptions = [] } = {} } = occupationConfig || {};

    return useMemo(() => {
        if (!withOccupation) {
            return {
                occupation: []
            };
        }

        const check = (value: string) => {
            const splitValue = value.split('-')[0].trim();
            const checkAdditionalOccupation = additionalOccupationOptions.some(({ option }) => {
                const op = option.trim().toLowerCase();

                return op === value || op === splitValue;
            });

            return checkAdditionalOccupation
                || occupationSuggestions.some(({ suggestion }: { suggestion: string }) => suggestion.toLowerCase() === value);
        };

        return {
            occupation: [
                fn({
                    fn: (input = '') => {
                        const value = input.trim().toLowerCase();
                        const isMandatoryCheck = isOccupationMandatory ? true : value.length > 0;

                        if (isMandatoryCheck && value.length < 2) {
                            return [t('occupation.length')];
                        }

                        if (value && !check(value)) {
                            return [t('occupation.notInList')];
                        }
                    }
                })
            ],
            otherOccupation: [
                required({ error: t('otherOccupation.required') }),
                length({ min: 3, max: 40, error: t('otherOccupation.length') }),
                regexp({ re: /^[a-zA-Z\s']+$/, error: t('otherOccupation.regexp') })
            ]
        };
    }, [additionalOccupationOptions, isOccupationMandatory, occupationSuggestions, t, withOccupation]);
};
